(() => {
  if (customElements.get('goose-cart-delivery')) return;
  const cache = new Map();
  const productSettings = async item => {
    const local = document.getElementById('GooseJohnModelGallery');
    if (local) {
      try {
        const config = JSON.parse(local.textContent);
        if (Object.hasOwn(config.variants || {}, String(item.variant))) return { config };
      } catch {}
    }
    const url = new URL(item.url, location.origin);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) throw Error('Invalid product URL');
    url.searchParams.set('variant', item.variant);
    const key = url.href;
    const previous = cache.get(key);
    if (previous && Date.now() - previous.time < 300000) return previous.value;
    const value = fetch(key, { credentials: 'same-origin', signal: AbortSignal.timeout(10000) }).then(async response => {
      if (!response.ok) throw Error('Delivery configuration unavailable');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const node = document.getElementById('GooseJohnModelGallery');
      if (!node) throw Error('Delivery configuration unavailable');
      return { config: JSON.parse(node.textContent) };
    });
    cache.set(key, { time: Date.now(), value });
    value.catch(() => cache.delete(key));
    return value;
  };
  const resolveRule = (item, context) => {
    const config = context.config;
    const material = String(config.variants[String(item.variant)]?.material || config.defaultMaterial || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    if (config.expressDelivery && config.isHat) {
      const preparation = /lana fina|fine wool|\b8x\b/.test(material) ? [2, 2] : /conejo|rabbit/.test(material) ? [4, 5] : /castor|beaver/.test(material) ? [12, 12] : null;
      if (preparation) return { express: true, preparation };
    }
    if (config.isHat && /castor|beaver/.test(material)) return { unavailable: true };
    if (config.fastDelivery) return { days: 5 };
    if (config.isHat && /lana fina|fine wool/.test(material)) return { days: 8 };
    if (config.isHat && /conejo|rabbit/.test(material)) return { days: 15 };
    throw Error('Delivery rule unavailable');
  };
  const fullDate = date => [String(date.getUTCDate()).padStart(2, '0'), String(date.getUTCMonth() + 1).padStart(2, '0'), date.getUTCFullYear()].join('/');
  const promiseProperties = value => {
    if (value.unavailable) return {};
    if (![value.min, value.max, value.preparation].every(date => date instanceof Date && Number.isFinite(date.getTime()))) {
      throw Error('No se pudo confirmar la fecha de una pieza. Revisa tu selección antes de pagar.');
    }
    return {
      _goose_delivery: 'true',
      _goose_delivery_version: '2',
      _goose_delivery_transit_business_days: '5',
      'Entrega prometida en México': 'Entre ' + fullDate(value.min) + ' y ' + fullDate(value.max) + '. Listo para envío: ' + fullDate(value.preparation)
    };
  };
  const deliveryWindow = (rule, country = 'MX') => {
    if (rule.unavailable) return { unavailable: true };
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
    const part = type => Number(parts.find(value => value.type === type).value);
    const origin = new Date(Date.UTC(part('year'), part('month') - 1, part('day'), 12));
    const add = count => {
      const result = new Date(origin);
      for (let remaining = count; remaining > 0;) {
        result.setUTCDate(result.getUTCDate() + 1);
        if (result.getUTCDay() !== 0 && result.getUTCDay() !== 6) remaining--;
      }
      return result;
    };
    if (rule.express) {
      const transit = country === 'US' ? [6, 6] : [4, 5];
      return {
        preparation: add(rule.preparation[1]),
        min: add(rule.preparation[0] + transit[0]),
        max: add(rule.preparation[1] + transit[1]),
        express: true,
        preparationDays: rule.preparation,
        transitDays: transit
      };
    }
    const transitDays = country === 'US' ? 6 : 5;
    const days = Math.max(0, rule.days - 5) + transitDays;
    const maximum = add(days + 1);
    const preparation = new Date(maximum);
    for (let remaining = transitDays; remaining > 0;) {
      preparation.setUTCDate(preparation.getUTCDate() - 1);
      if (preparation.getUTCDay() !== 0 && preparation.getUTCDay() !== 6) remaining--;
    }
    return { preparation, min: add(days), max: maximum };
  };
  class GooseCartDelivery extends HTMLElement {
    async connectedCallback() {
      if (!this.cartUpdateUnsubscriber && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData }) => {
          const footer = document.getElementById('main-cart-footer');
          const html = cartData?.sections?.[footer?.dataset.id] || cartData?.sections?.['cart-drawer'];
          if (!html) return;
          const updated = new DOMParser().parseFromString(html, 'text/html').querySelector('goose-cart-delivery');
          if (!updated) return;
          for (const selector of ['[data-delivery-shipping]', '[data-delivery-config]']) {
            const current = this.querySelector(selector);
            const replacement = updated.querySelector(selector);
            if (current && replacement) current.replaceWith(replacement);
          }
          this.connectedCallback();
        });
      }
      const generation = this.gmsGeneration = (this.gmsGeneration || 0) + 1;
      this.dataset.orderPromises = '';
      const source = this.querySelector('[data-delivery-config]');
      if (!source) return;
      const config = JSON.parse(source.textContent);
      const items = config.items.filter(item => item.shipping);
      if (!items.length) { this.hidden = true; return; }
      const label = this.querySelector('[data-delivery-estimate]');
      const link = this.querySelector('a');
      const details = this.querySelector('[data-delivery-breakdown]');
      const fallback = () => {
        if (details) { details.textContent = ''; details.style.display = 'none'; }
        label.textContent = config.estimated;
        link.textContent = config.unavailable;
        this.dataset.state = 'unavailable';
      };
      if (!['MX', 'US'].includes(this.dataset.country)) { fallback(); return; }
      try {
        const unique = [...new Map(items.map(item => [item.variant, item])).values()];
        const windows = await Promise.all(unique.map(async item => {
          const context = await productSettings(item);
          return { variant: String(item.variant), ...deliveryWindow(resolveRule(item, context), this.dataset.country) };
        }));
        if (!this.isConnected || generation !== this.gmsGeneration) return;
        if (details) {
          const express = windows.every(value => value.express);
          const rangeText = values => values[0] === values[1] ? String(values[0]) : values.join('–');
          const preparation = express ? [Math.max(...windows.map(value => value.preparationDays[0])), Math.max(...windows.map(value => value.preparationDays[1]))] : null;
          const text = express
            ? rangeText(preparation) + ' ' + config.preparationDaysLabel + ' + ' + rangeText(windows[0].transitDays) + ' ' + config.transitDaysLabel + '. ' + config.businessDaysLabel + '.'
            : this.dataset.country === 'US' ? config.usaShippingDetails : '';
          details.textContent = text || '';
          details.style.display = text ? 'block' : 'none';
        }
        if (windows.some(window => window.unavailable)) {
          label.textContent = this.dataset.country === 'US' ? config.usaVerifyAvailability : config.verifyAvailability;
          this.dataset.state = 'ready';
          this.dataset.orderPromises = '{}';
          return;
        }
        const minimum = new Date(Math.max(...windows.map(window => window.min.getTime())));
        const maximum = new Date(Math.max(...windows.map(window => window.max.getTime())));
        const format = date => new Intl.DateTimeFormat(config.dateLocale || this.dataset.locale || 'es', { day: 'numeric', month: 'short', timeZone: 'UTC', ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }) }).format(date);
        const dates = minimum.getTime() === maximum.getTime() ? config.on + ' ' + format(minimum) : config.between + ' ' + format(minimum) + ' ' + config.and + ' ' + format(maximum);
        label.textContent = (this.dataset.country === 'US' ? config.usaToday : config.today) + ' ' + dates;
        this.dataset.state = 'ready';
        this.dataset.minimum = minimum.toISOString();
        this.dataset.maximum = maximum.toISOString();
        this.dataset.orderPromises = this.dataset.country === 'MX' ? JSON.stringify(Object.fromEntries(windows.map(value => [value.variant, promiseProperties(value)]))) : '{}';
      } catch { if (this.isConnected && generation === this.gmsGeneration) fallback(); }
    }

    disconnectedCallback() {
      this.cartUpdateUnsubscriber?.();
      this.cartUpdateUnsubscriber = undefined;
    }
  }
  GooseCartDelivery.captureForItems = async function(items, country) {
    if (country !== 'MX') return {};
    const windows = await Promise.all(items.filter(item => item.shipping).map(async item => {
      const context = await productSettings(item);
      return { variant: String(item.variant), ...deliveryWindow(resolveRule(item, context)) };
    }));
    return Object.fromEntries(windows.map(value => [value.variant, promiseProperties(value)]));
  };
  GooseCartDelivery.prototype.captureProperties = async function() {
    await this.connectedCallback();
    if (this.dataset.country !== 'MX') return {};
    if (this.dataset.state !== 'ready' || !this.dataset.orderPromises) throw Error('No se pudo confirmar la fecha de entrega. Revisa tu selección o solicita ayuda antes de pagar.');
    return JSON.parse(this.dataset.orderPromises);
  };
  class GooseCartTicker extends HTMLElement {
    connectedCallback() {
      const toggle = this.querySelector('button');
      if (!toggle || this.bound) return;
      this.bound = true;
      toggle.addEventListener('click', () => {
        const paused = this.dataset.paused !== 'true';
        this.dataset.paused = String(paused);
        toggle.setAttribute('aria-pressed', String(paused));
      });
    }
  }
  customElements.define('goose-cart-delivery', GooseCartDelivery);
  customElements.define('goose-cart-ticker', GooseCartTicker);
})();

